export interface CampaignGroupOption {
  id: string;
  name: string;
  isPrimary?: boolean;
}

export interface RuntimeConfig {
  VITE_API_BASE_URL?: string;
  VITE_RELEVANT_WINDOW_DAYS?: string;
  VITE_FRESHNESS_OFFSET_DAYS?: string;
  VITE_APP_NAME?: string;
  VITE_ASA_CAMPAIGN_GROUPS?: CampaignGroupOption[];
}

declare global {
  interface Window {
    __SEARCHADS_RUNTIME_CONFIG__?: RuntimeConfig;
  }
}

export const resolveConfigValue = (
  runtimeValue: string | undefined,
  buildValue: string | undefined
): string | undefined => {
  if (typeof runtimeValue === 'string' && runtimeValue.length > 0) {
    return runtimeValue;
  }

  if (typeof buildValue === 'string' && buildValue.length > 0) {
    return buildValue;
  }

  return undefined;
};

export const getRuntimeConfig = (): RuntimeConfig =>
  typeof window === 'undefined' ? {} : window.__SEARCHADS_RUNTIME_CONFIG__ ?? {};

type ScalarRuntimeConfigKey = Exclude<keyof RuntimeConfig, 'VITE_ASA_CAMPAIGN_GROUPS'>;

export const getConfigValue = (key: ScalarRuntimeConfigKey): string | undefined =>
  resolveConfigValue(getRuntimeConfig()[key], import.meta.env[key]);

const isCampaignGroupOption = (value: unknown): value is CampaignGroupOption => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as { id?: unknown; name?: unknown };
  return (
    typeof candidate.id === 'string' &&
    candidate.id.trim().length > 0 &&
    typeof candidate.name === 'string' &&
    candidate.name.trim().length > 0 &&
    (typeof (candidate as { isPrimary?: unknown }).isPrimary === 'undefined' ||
      typeof (candidate as { isPrimary?: unknown }).isPrimary === 'boolean')
  );
};

export const getCampaignGroupsConfig = (): CampaignGroupOption[] => {
  const groups = getRuntimeConfig().VITE_ASA_CAMPAIGN_GROUPS;
  if (!Array.isArray(groups)) {
    return [];
  }

  return groups.filter(isCampaignGroupOption).map((group) => ({
    id: group.id.trim(),
    name: group.name.trim(),
    isPrimary: group.isPrimary === true,
  }));
};
